/**
 * Sales Forecast Upload (`docs/UTILITIES.md` §6).
 *
 * One page, top to bottom in the order the job is done:
 *
 * 1. **Loaded now** — every current version, so the user sees what is there
 *    before choosing a file.
 * 2. **Upload** — the version type (a picker, never free text) and the file.
 * 3. **Preview** — blockers, warnings, the month-by-month comparison, and the
 *    placeholder question. Nothing has changed yet, and the page says so.
 * 4. **Confirm** — one dialog repeating the one consequential sentence.
 * 5. **Load history** — every load ever, each downloadable in the upload
 *    format. That download is the undo (D6).
 *
 * The server decides everything that matters — what blocks, what is current,
 * whether the version moved since the preview. This page only shows it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Download, FileUp } from 'lucide-react'
import { useId, useState } from 'react'

import { Badge, Button, Card, ConfirmDialog, Grid } from '../../components/admin/ui'
import { ErrorState, Skeleton } from '../../components/States'
import { EMPTY, formatDateTime, formatNumber } from '../../lib/format'
import type { UtilityProps } from '../index'
import type {
  CommitResult,
  ComparisonTotal,
  Issue,
  Load,
  MonthComparison,
  Preview,
} from './api'
import { forecastApi } from './api'
import { consequence, versionName, whole } from './text'

const LANDING_KEY = ['utility', 'sales-forecast-upload'] as const

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Oct 2026" from an ISO date, without the viewer's time zone moving the day. */
function monthLabel(iso: string): string {
  const [year, month] = iso.split('-')
  return `${MONTHS[Number(month) - 1] ?? '?'} ${year ?? ''}`
}

const money = (value: number | null | undefined) =>
  value === null || value === undefined ? EMPTY : `$${whole(value)}`

/** "+1,234" / "−1,234" / "0", with a true minus sign so the column aligns. */
function signed(value: number | null | undefined, format: (v: number) => string): string {
  if (value === null || value === undefined) return EMPTY
  const shown = format(Math.abs(value))
  if (shown === '0' || shown === '$0' || shown === '0.0%') return shown
  return `${value > 0 ? '+' : '−'}${shown}`
}

function Num({ children, muted = false }: { children: string; muted?: boolean }) {
  return (
    <td
      className={`numeric px-2 py-1 text-right whitespace-nowrap ${
        muted ? 'text-[var(--color-ink-muted)]' : ''
      }`}
    >
      {children}
    </td>
  )
}

export function SalesForecastUpload(_props: UtilityProps) {
  const landing = useQuery({ queryKey: LANDING_KEY, queryFn: forecastApi.landing })
  const [preview, setPreview] = useState<Preview | null>(null)
  const [result, setResult] = useState<CommitResult | null>(null)

  if (landing.isPending) return <Skeleton rows={4} />
  // Only a first load that failed replaces the page. A failed refresh (the
  // re-read after a confirm, say) keeps what is on screen -- including the
  // "Loaded" panel -- and says the list may be out of date beside it.
  if (landing.data === undefined) {
    return <ErrorState error={landing.error} onRetry={() => void landing.refetch()} />
  }

  const current = landing.data.loads.filter((load) => load.is_current)

  return (
    <div className="space-y-6">
      {landing.error !== null && (
        <ErrorState error={landing.error} onRetry={() => void landing.refetch()} />
      )}
      <Card
        title="Loaded now"
        description="The current version of each forecast. An upload replaces only the version with the same type and fiscal year; every other version is left alone."
      >
        <LoadTable loads={current} empty="Nothing is loaded yet." />
      </Card>

      {result !== null ? (
        <LoadedPanel
          result={result}
          onDone={() => {
            setResult(null)
          }}
        />
      ) : (
        <UploadCard
          fcstTypes={landing.data.fcst_types}
          maxBytes={landing.data.max_upload_bytes}
          hasPreview={preview !== null}
          onPreview={setPreview}
        />
      )}

      {preview !== null && result === null && (
        <PreviewPanel
          // A new preview is a new decision: reset the ticks and the dialog.
          key={preview.sha256 + (preview.staging_token ?? '')}
          preview={preview}
          ttlMinutes={Math.round(landing.data.staging_ttl_seconds / 60)}
          onCancel={() => {
            setPreview(null)
          }}
          onLoaded={(loaded) => {
            setPreview(null)
            setResult(loaded)
          }}
        />
      )}

      <Card
        title="Load history"
        description="Every load, including replaced ones. Download writes a load back out in the upload format: to undo a load, download the version it replaced and upload that."
      >
        <LoadTable loads={landing.data.loads} empty="No loads yet." history />
      </Card>
    </div>
  )
}

function LoadTable({
  loads,
  empty,
  history = false,
}: {
  loads: readonly Load[]
  empty: string
  history?: boolean
}) {
  if (loads.length === 0) {
    return <p className="text-[length:var(--text-sm)] text-[var(--color-ink-muted)]">{empty}</p>
  }
  const head = [
    'Version',
    'Months',
    'Lines',
    'Gallons',
    'Cases',
    'FOB',
    'File',
    'Loaded',
    ...(history ? ['Status'] : []),
    '',
  ]
  return (
    <Grid head={head}>
      {loads.map((load) => (
        <tr key={load.load_id} className="border-b border-[var(--color-rule)]/60 align-top">
          <td className="px-2 py-1.5 font-medium whitespace-nowrap">
            {versionName(load.fcst, load.fiscal_year)}
          </td>
          <td className="px-2 py-1.5 whitespace-nowrap">
            {monthLabel(load.first_period)} – {monthLabel(load.last_period)}
          </td>
          <Num>{whole(load.lines)}</Num>
          <Num>{whole(load.gal)}</Num>
          <Num>{whole(load.cases)}</Num>
          <Num>{money(load.fob)}</Num>
          <td className="max-w-[16rem] truncate px-2 py-1.5" title={load.source_file}>
            {load.source_file}
          </td>
          <td className="px-2 py-1.5 whitespace-nowrap">
            <span className="numeric">{formatDateTime(load.loaded_at)}</span>
            <span className="block text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
              {load.loaded_by}
            </span>
          </td>
          {history && (
            <td className="px-2 py-1.5">
              {load.is_current ? (
                <Badge tone="positive">Current</Badge>
              ) : (
                <Badge>
                  Replaced
                  {load.superseded_by !== null ? ` by load ${String(load.superseded_by)}` : ''}
                </Badge>
              )}
            </td>
          )}
          <td className="px-2 py-1.5 text-right">
            <a
              href={forecastApi.downloadUrl(load.load_id)}
              className="inline-flex items-center gap-1 text-[length:var(--text-xs)] text-[var(--accent)] hover:underline"
              aria-label={`Download load ${String(load.load_id)}`}
            >
              <Download aria-hidden className="size-3.5" />
              Download
            </a>
          </td>
        </tr>
      ))}
    </Grid>
  )
}

function UploadCard({
  fcstTypes,
  maxBytes,
  hasPreview,
  onPreview,
}: {
  fcstTypes: readonly string[]
  maxBytes: number
  hasPreview: boolean
  onPreview: (preview: Preview | null) => void
}) {
  const typeId = useId()
  const fileId = useId()
  const [fcst, setFcst] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const mutation = useMutation({
    mutationFn: ({ type, upload }: { type: string; upload: File }) =>
      forecastApi.preview(type, upload),
    onSuccess: onPreview,
  })
  const tooBig = file !== null && file.size > maxBytes
  const ready = file !== null && fcst !== '' && !tooBig

  return (
    <Card
      title="Upload a forecast"
      description="Pick the version type, choose the Netstock upload workbook (sku | shipto | period | FOB | Cases | Gal), and preview it. Nothing changes until you confirm."
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (!ready) return
          onPreview(null)
          mutation.mutate({ type: fcst, upload: file })
        }}
      >
        <label htmlFor={typeId} className="block">
          <span className="block text-[length:var(--text-xs)] font-medium">Version type</span>
          <select
            id={typeId}
            value={fcst}
            onChange={(event) => {
              setFcst(event.target.value)
            }}
            className="mt-1 rounded-[var(--radius-md)] border border-[var(--color-rule)] bg-[var(--color-surface)] px-2 py-1.5 text-[length:var(--text-sm)]"
          >
            <option value="">Choose…</option>
            {fcstTypes.map((type) => (
              <option key={type} value={type}>
                {type === 'BUD' ? 'BUD — budget' : `${type} — revision`}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={fileId} className="block">
          <span className="block text-[length:var(--text-xs)] font-medium">Workbook (.xlsx)</span>
          <input
            id={fileId}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null)
            }}
            // The browser's own picker button is unstyled text in our ink and read as a
            // label, not a control (owner, 2026-11-04): box it and give the button the accent.
            className="mt-1 block w-72 max-w-full cursor-pointer rounded-[var(--radius-md)] border border-dashed border-[var(--accent)] bg-[var(--color-surface)] px-2 py-1 text-[length:var(--text-sm)] text-[var(--color-ink-muted)] hover:bg-[var(--accent)]/5 file:mr-3 file:cursor-pointer file:rounded-[var(--radius-md)] file:border file:border-solid file:border-[var(--accent)] file:bg-[var(--color-surface-raised)] file:px-2.5 file:py-1 file:text-[length:var(--text-xs)] file:font-medium file:text-[var(--accent)] hover:file:bg-[var(--accent)] hover:file:text-[var(--color-surface-raised)]"
          />
        </label>
        <Button type="submit" variant="primary" disabled={!ready || mutation.isPending}>
          <FileUp aria-hidden className="size-3.5" />
          {mutation.isPending ? 'Checking…' : hasPreview ? 'Preview again' : 'Preview'}
        </Button>
      </form>
      {tooBig && (
        <p role="alert" className="mt-2 text-[length:var(--text-xs)] text-[var(--color-negative)]">
          That file is larger than {String(Math.round(maxBytes / 1024 / 1024))} MB, which is not a
          Netstock upload.
        </p>
      )}
      {mutation.error !== null && (
        <div className="mt-3">
          <ErrorState error={mutation.error} />
        </div>
      )}
    </Card>
  )
}

function PreviewPanel({
  preview,
  ttlMinutes,
  onCancel,
  onLoaded,
}: {
  preview: Preview
  ttlMinutes: number
  onCancel: () => void
  onLoaded: (result: CommitResult) => void
}) {
  const queryClient = useQueryClient()
  const [ticked, setTicked] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        preview.placeholder_candidates
          .filter((candidate) => candidate.suggested)
          .map((candidate) => candidate.sku),
      ),
  )
  const [confirming, setConfirming] = useState(false)
  const commit = useMutation({
    mutationFn: () => forecastApi.commit(preview, [...ticked].sort()),
    onSuccess: (loaded) => {
      // The load has committed: say so first. Re-reading the list is a
      // separate request that can fail or retry on its own, and the result of
      // a write must never wait on it (2026-10-17: a 502 on the re-read made a
      // successful load look like a failed one).
      onLoaded(loaded)
      void queryClient.invalidateQueries({ queryKey: LANDING_KEY })
    },
  })

  const blockers = preview.issues.filter((issue) => issue.severity === 'blocker')
  const warnings = preview.issues.filter((issue) => issue.severity === 'warning')
  const comparison = preview.comparison
  const canLoad = !preview.blocked && preview.staging_token !== null
  const name =
    preview.fiscal_year !== null ? versionName(preview.fcst, preview.fiscal_year) : preview.fcst
  const replacing = preview.current_load !== null
  const sheet = preview.sheet !== null ? ` · sheet “${preview.sheet}”` : ''

  return (
    <Card
      title={`Preview: ${name}`}
      description={`${preview.source_file}${sheet} · nothing has been changed yet.`}
      actions={
        <>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!canLoad || commit.isPending}
            onClick={() => {
              setConfirming(true)
            }}
          >
            {commit.isPending ? 'Loading…' : replacing ? 'Replace…' : 'Load…'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {blockers.length > 0 && (
          <IssueList
            tone="danger"
            title="This file cannot be loaded until these are fixed"
            issues={blockers}
          />
        )}
        {warnings.length > 0 && (
          <IssueList tone="warning" title="Check before loading" issues={warnings} />
        )}
        {blockers.length === 0 && warnings.length === 0 && (
          <p className="text-[length:var(--text-sm)] text-[var(--color-positive)]">
            No problems found in the file.
          </p>
        )}

        {comparison !== null && (
          <ComparisonTable
            months={comparison.months}
            total={comparison.total}
            hasCurrent={comparison.has_current}
          />
        )}

        {preview.placeholder_candidates.length > 0 && (
          <fieldset className="rounded-[var(--radius-md)] border border-[var(--color-rule)] p-3">
            <legend className="px-1 text-[length:var(--text-xs)] font-medium">
              Placeholder SKUs?
            </legend>
            <p className="text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
              These SKUs are not in the SKU map, or were flagged as placeholders in an earlier load
              (those are ticked already). Tick the ones that are placeholders — a prospect or open
              territory rather than a real item. Every line is loaded and counted either way; the
              tick only decides how reports attribute it.
            </p>
            <ul className="mt-2 grid gap-1 sm:grid-cols-2">
              {preview.placeholder_candidates.map((candidate) => (
                <li key={candidate.sku}>
                  <label className="flex items-baseline gap-2 text-[length:var(--text-sm)]">
                    <input
                      type="checkbox"
                      checked={ticked.has(candidate.sku)}
                      disabled={!canLoad}
                      onChange={(event) => {
                        const next = new Set(ticked)
                        if (event.target.checked) next.add(candidate.sku)
                        else next.delete(candidate.sku)
                        setTicked(next)
                      }}
                    />
                    <span className="font-medium">{candidate.sku}</span>
                    <span className="numeric text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
                      {whole(candidate.lines)} lines · {whole(candidate.gal)} gal
                      {candidate.flagged_before ? ' · flagged before' : ''}
                      {candidate.in_sku_map ? '' : ' · not in SKU map'}
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}

        {canLoad && (
          <p className="text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
            This preview can be confirmed for {String(ttlMinutes)} minutes. If someone else loads{' '}
            {name} in the meantime, the confirm is refused and nothing changes.
          </p>
        )}
        {commit.error !== null && <ErrorState error={commit.error} />}
      </div>

      <ConfirmDialog
        open={confirming}
        title={replacing ? `Replace ${name}?` : `Load ${name}?`}
        body={consequence(preview)}
        confirmLabel={replacing ? 'Replace' : 'Load'}
        onCancel={() => {
          setConfirming(false)
        }}
        onConfirm={() => {
          setConfirming(false)
          commit.mutate()
        }}
      />
    </Card>
  )
}

function IssueList({
  tone,
  title,
  issues,
}: {
  tone: 'danger' | 'warning'
  title: string
  issues: readonly Issue[]
}) {
  const colour =
    tone === 'danger'
      ? 'border-[var(--color-negative)]/40 bg-[var(--color-negative)]/5 text-[var(--color-negative)]'
      : 'border-[#b8860b]/40 bg-[#b8860b]/5 text-[#b8860b]'
  return (
    <div
      role={tone === 'danger' ? 'alert' : undefined}
      className={`rounded-[var(--radius-md)] border p-3 ${colour}`}
    >
      <p className="text-[length:var(--text-sm)] font-medium">{title}</p>
      <ul className="mt-1 space-y-2">
        {issues.map((issue) => (
          <li key={issue.code} className="text-[length:var(--text-sm)] text-[var(--color-ink)]">
            {issue.message}
            {issue.count !== null && issue.count > issue.examples.length && (
              <span className="text-[var(--color-ink-muted)]"> ({whole(issue.count)} in all)</span>
            )}
            {issue.examples.length > 0 && (
              <ul className="numeric mt-0.5 list-disc pl-5 text-[length:var(--text-xs)] text-[var(--color-ink-muted)]">
                {issue.examples.map((example) => (
                  <li key={example}>{example}</li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

const COMPARE_HEAD = [
  'Month',
  'Lines now',
  'Lines file',
  'Gal now',
  'Gal file',
  'Δ gal',
  'Δ %',
  'Cases now',
  'Cases file',
  'Δ cases',
  'FOB now',
  'FOB file',
  'Δ FOB',
] as const

const FILE_ONLY_HEAD = ['Month', 'Lines', 'Gallons', 'Cases', 'FOB'] as const

function ComparisonTable({
  months,
  total,
  hasCurrent,
}: {
  months: readonly MonthComparison[]
  total: ComparisonTotal
  hasCurrent: boolean
}) {
  const row = (
    key: string,
    label: string,
    totals: ComparisonTotal,
    { muted = false, strong = false }: { muted?: boolean; strong?: boolean } = {},
  ) => {
    const { current, proposed, difference } = totals
    return (
      <tr
        key={key}
        className={`border-b border-[var(--color-rule)]/60 ${strong ? 'font-semibold' : ''} ${
          muted ? 'text-[var(--color-ink-muted)]' : ''
        }`}
      >
        <td className="px-2 py-1 whitespace-nowrap">{label}</td>
        {hasCurrent ? (
          <>
            <Num muted>{whole(current?.lines)}</Num>
            <Num>{whole(proposed.lines)}</Num>
            <Num muted>{whole(current?.gal)}</Num>
            <Num>{whole(proposed.gal)}</Num>
            <Num>{signed(difference?.gal, whole)}</Num>
            <Num>
              {signed(difference?.gal_pct, (v) => `${formatNumber(v, { decimals: 1 })}%`)}
            </Num>
            <Num muted>{whole(current?.cases)}</Num>
            <Num>{whole(proposed.cases)}</Num>
            <Num>{signed(difference?.cases, whole)}</Num>
            <Num muted>{money(current?.fob)}</Num>
            <Num>{money(proposed.fob)}</Num>
            <Num>{signed(difference?.fob, money)}</Num>
          </>
        ) : (
          <>
            <Num>{whole(proposed.lines)}</Num>
            <Num>{whole(proposed.gal)}</Num>
            <Num>{whole(proposed.cases)}</Num>
            <Num>{money(proposed.fob)}</Num>
          </>
        )}
      </tr>
    )
  }

  return (
    <div>
      <h3 className="mb-1 text-[length:var(--text-sm)] font-semibold">
        {hasCurrent ? 'Loaded now against this file, by month' : 'This file, by month'}
      </h3>
      <Grid head={hasCurrent ? COMPARE_HEAD : FILE_ONLY_HEAD}>
        {months.map((month) =>
          row(month.period, month.label, month, { muted: !month.in_window }),
        )}
        {row('total', 'Total', total, { strong: true })}
      </Grid>
      <p className="mt-1 text-[length:var(--text-2xs)] text-[var(--color-ink-muted)]">
        Greyed months are outside this version type&apos;s months. Differences are the file minus
        what is loaded now. Gallons, cases and FOB are rounded to whole units for display.
      </p>
    </div>
  )
}

function LoadedPanel({ result, onDone }: { result: CommitResult; onDone: () => void }) {
  const { load, replaced } = result
  const name = versionName(load.fcst, load.fiscal_year)
  return (
    <Card
      title={`Loaded: ${name}`}
      description={`Load ${String(load.load_id)} · ${load.source_file} · ${formatDateTime(load.loaded_at)} by ${load.loaded_by}`}
      actions={
        <Button variant="primary" onClick={onDone}>
          Done
        </Button>
      }
    >
      <p className="text-[length:var(--text-sm)]">
        {whole(load.lines)} lines and {whole(load.gal)} gal are now the current {name}.
        {replaced !== null &&
          ` Load ${String(replaced.load_id)} (${whole(result.archived_lines)} lines) was moved ` +
            'to the archive and can be downloaded from the load history.'}{' '}
        The Month-End Forecast reads the new plan on its next view.
      </p>
      <div className="mt-3">
        <Grid head={FILE_ONLY_HEAD}>
          {result.months.map((month) => (
            <tr key={month.period} className="border-b border-[var(--color-rule)]/60">
              <td className="px-2 py-1">{month.label}</td>
              <Num>{whole(month.lines)}</Num>
              <Num>{whole(month.gal)}</Num>
              <Num>{whole(month.cases)}</Num>
              <Num>{money(month.fob)}</Num>
            </tr>
          ))}
        </Grid>
      </div>
    </Card>
  )
}
